import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Users,
  FileText,
  Plus,
  Loader2,
  RefreshCw,
  FileIcon,
  Video,
  ExternalLink,
  MoreVertical,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  MessageSquare,
  Calendar,
  Send,
  HelpCircle,
  X,
} from "lucide-react";
import {
  getSchoolNotificationContacts,
  getSchoolDeliverables,
  Deliverable,
  deleteDeliverable,
  updateDeliverable,
  getSchoolQueries,
  createAdminQuery,
  updateQuery,
  ClientQuery,
  getQueryComments,
  addQueryComment,
  QueryComment,
  getQueryCategories,
  getQueryOwners,
  uploadAdminQueryAttachments,
  QueryCategory,
  QueryOwner,
  QueryAttachment,
} from "@/actions/delivery-hub";
import { AttachmentList, FilePicker } from "@/components/queries/QueryAttachments";
import QueryRoutingControls from "@/components/queries/QueryRoutingControls";
import { useDeliveryHub } from "@/contexts/DeliveryHubContext";
import NotificationContactsModal from "@/components/delivery-hub/NotificationContactsModal";
import AddDeliverableSlider from "@/components/delivery-hub/AddDeliverableSlider";
import DeliverableDetailSlider from "@/components/delivery-hub/DeliverableDetailSlider";
import NotifyClientsSlider from "@/components/delivery-hub/NotifyClientsSlider";
import { showToast } from "@/components/ui/Toast";

// Helper: convert URLs in text to clickable links
const linkifyText = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="text-brand underline hover:text-brand-hover break-all"
        onClick={(e) => e.stopPropagation()}
      >
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    )
  );
};

// Helper functions
const getTypeIcon = (type: Deliverable["type"]) => {
  switch (type) {
    case "blog":
      return <FileIcon size={20} className="text-brand" />;
    case "video":
      return <Video size={20} className="text-pink-600" />;
  }
};

const getStatusBadge = (status: Deliverable["status"]) => {
  switch (status) {
    case "approved":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
          <CheckCircle size={12} />
          Approved
        </span>
      );
    case "pending":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
          <Clock size={12} />
          Pending
        </span>
      );
    case "needs_revision":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
          <AlertCircle size={12} />
          Needs Revision
        </span>
      );
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const getQueryStatusBadge = (status: string) => {
  switch (status) {
    case "resolved":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
          <CheckCircle size={12} />
          Resolved
        </span>
      );
    case "in_progress":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
          <Clock size={12} />
          In Progress
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
          <AlertCircle size={12} />
          Open
        </span>
      );
  }
};

const monthNames = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const SchoolDetail = () => {
  const { schoolId } = useParams<{ schoolId: string }>();
  const { getSchoolById, loading: schoolsLoading, updateSchoolCounts } = useDeliveryHub();
  const [contactsCount, setContactsCount] = useState(0);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [deliverablesLoading, setDeliverablesLoading] = useState(true);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [showAddDeliverable, setShowAddDeliverable] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [monthFilter, setMonthFilter] = useState<string>("all");
  const [yearFilter, setYearFilter] = useState<string>(String(new Date().getFullYear()));
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [selectedDeliverable, setSelectedDeliverable] = useState<Deliverable | null>(null);
  const [deliverableToNotify, setDeliverableToNotify] = useState<Deliverable | null>(null);

  // Tabs and queries state
  const [activeTab, setActiveTab] = useState<"deliverables" | "queries">("deliverables");
  const [queries, setQueries] = useState<ClientQuery[]>([]);
  const [queriesLoading, setQueriesLoading] = useState(true);
  const [selectedQuery, setSelectedQuery] = useState<ClientQuery | null>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [updatingQuery, setUpdatingQuery] = useState(false);

  // Raise query modal state
  const [showRaiseQuery, setShowRaiseQuery] = useState(false);
  const [newQuerySubject, setNewQuerySubject] = useState("");
  const [newQueryMessage, setNewQueryMessage] = useState("");
  const [newQueryCategoryId, setNewQueryCategoryId] = useState<number | "">("");
  const [newQueryFiles, setNewQueryFiles] = useState<File[]>([]);
  const [creatingQuery, setCreatingQuery] = useState(false);

  // Query routing (category → owner bucket)
  const [queryCategories, setQueryCategories] = useState<QueryCategory[]>([]);
  const [queryOwners, setQueryOwners] = useState<QueryOwner[]>([]);

  // Query comments state
  const [expandedQuery, setExpandedQuery] = useState<number | null>(null);
  const [queryComments, setQueryComments] = useState<QueryComment[]>([]);
  const [loadingQueryComments, setLoadingQueryComments] = useState(false);
  const [queryCommentText, setQueryCommentText] = useState("");
  const [queryCommentFiles, setQueryCommentFiles] = useState<File[]>([]);
  const [addingQueryComment, setAddingQueryComment] = useState(false);

  const school = schoolId ? getSchoolById(schoolId) : undefined;

  useEffect(() => {
    getQueryCategories().then(setQueryCategories);
    getQueryOwners().then(setQueryOwners);
  }, []);

  const replaceQuery = (updated: ClientQuery) =>
    setQueries((prev) => prev.map((q) => (q.id === updated.id ? updated : q)));

  /** Upload picked files; returns null (and shows an error) if the upload failed */
  const uploadFiles = async (files: File[]): Promise<QueryAttachment[] | null> => {
    if (files.length === 0) return [];
    const upload = await uploadAdminQueryAttachments(files);
    if (!upload.success) {
      showToast(upload.message || "Failed to upload files", "error");
      return null;
    }
    return upload.attachments;
  };

  const resetRaiseQuery = () => {
    setShowRaiseQuery(false);
    setNewQuerySubject("");
    setNewQueryMessage("");
    setNewQueryCategoryId("");
    setNewQueryFiles([]);
  };

  // Fetch data when schoolId changes
  useEffect(() => {
    if (!schoolId) return;

    const fetchData = async () => {
      setContactsLoading(true);
      setDeliverablesLoading(true);
      setQueriesLoading(true);

      const [contactsData, deliverablesData, queriesData] = await Promise.all([
        getSchoolNotificationContacts(Number(schoolId)),
        getSchoolDeliverables(Number(schoolId)),
        getSchoolQueries(Number(schoolId)),
      ]);

      setContactsCount(contactsData.count);
      setDeliverables(deliverablesData);
      setQueries(queriesData.queries);
      setContactsLoading(false);
      setDeliverablesLoading(false);
      setQueriesLoading(false);
    };

    fetchData();
  }, [schoolId]);

  const loading = schoolsLoading || contactsLoading || deliverablesLoading;
  const openQueriesCount = queries.filter(q => q.status === "open").length;

  const handleRefresh = async () => {
    setRefreshing(true);
    const [contactsData, deliverablesData, queriesData] = await Promise.all([
      getSchoolNotificationContacts(Number(schoolId)),
      getSchoolDeliverables(Number(schoolId)),
      getSchoolQueries(Number(schoolId)),
    ]);
    setContactsCount(contactsData.count);
    setDeliverables(deliverablesData);
    setQueries(queriesData.queries);
    setRefreshing(false);
  };

  // Handle query status change
  const handleQueryStatusChange = async (queryId: number, newStatus: string, notes?: string) => {
    const query = queries.find((q) => q.id === queryId);
    const oldStatus = query?.status;

    setUpdatingQuery(true);
    const result = await updateQuery(queryId, {
      status: newStatus,
      ...(notes && { admin_notes: notes }),
    });
    if (result.success && result.query) {
      setQueries((prev) =>
        prev.map((q) => (q.id === queryId ? result.query! : q))
      );
      showToast(`Query marked as ${newStatus.replace("_", " ")}`, "success");
      setSelectedQuery(null);
      setAdminNotes("");

      // Update sidebar count locally (optimistic update)
      // If going from open -> resolved/in_progress, decrease query count
      // If going from resolved -> open, increase query count
      if (oldStatus === "open" && newStatus !== "open") {
        updateSchoolCounts(Number(schoolId), { query: -1 });
      } else if (oldStatus !== "open" && newStatus === "open") {
        updateSchoolCounts(Number(schoolId), { query: 1 });
      }
    } else {
      showToast(result.message || "Failed to update query", "error");
    }
    setUpdatingQuery(false);
  };

  // Fetch comments for a query
  const fetchQueryComments = async (queryId: number) => {
    setLoadingQueryComments(true);
    try {
      const result = await getQueryComments(queryId);
      setQueryComments(result.comments);
    } catch (error) {
      console.error("Error fetching query comments:", error);
    } finally {
      setLoadingQueryComments(false);
    }
  };

  // Toggle query comments expansion
  const toggleQueryComments = (queryId: number) => {
    setQueryCommentFiles([]);
    if (expandedQuery === queryId) {
      setExpandedQuery(null);
      setQueryComments([]);
    } else {
      setExpandedQuery(queryId);
      fetchQueryComments(queryId);
    }
  };

  // Add comment to query
  const handleAddQueryComment = async () => {
    if ((!queryCommentText.trim() && queryCommentFiles.length === 0) || !expandedQuery) return;

    setAddingQueryComment(true);
    try {
      const attachments = await uploadFiles(queryCommentFiles);
      if (!attachments) return;
      const result = await addQueryComment(expandedQuery, queryCommentText.trim(), attachments);
      if (result.success && result.comment) {
        setQueryComments((prev) => [...prev, result.comment!]);
        setQueryCommentText("");
        setQueryCommentFiles([]);
        showToast("Comment added", "success");
      } else {
        showToast(result.message || "Failed to add comment", "error");
      }
    } catch (error) {
      console.error("Error adding query comment:", error);
      showToast("Failed to add comment", "error");
    } finally {
      setAddingQueryComment(false);
    }
  };

  const handleCreateQuery = async () => {
    if (!newQuerySubject.trim() || !newQueryMessage.trim()) return;
    setCreatingQuery(true);
    const attachments = await uploadFiles(newQueryFiles);
    if (!attachments) {
      setCreatingQuery(false);
      return;
    }
    const result = await createAdminQuery(Number(schoolId), {
      subject: newQuerySubject.trim(),
      message: newQueryMessage.trim(),
      ...(newQueryCategoryId && { category_id: Number(newQueryCategoryId) }),
      attachments,
    });
    if (result.success && result.query) {
      setQueries((prev) => [result.query!, ...prev]);
      showToast("Query created successfully", "success");
      resetRaiseQuery();
      updateSchoolCounts(Number(schoolId), { query: 1 });
    } else {
      showToast(result.message || "Failed to create query", "error");
    }
    setCreatingQuery(false);
  };

  const handleDeliverableCreated = async (deliverable: Deliverable) => {
    setShowAddDeliverable(false);
    // Refresh deliverables list
    const deliverablesData = await getSchoolDeliverables(Number(schoolId));
    setDeliverables(deliverablesData);
    // Open notification slider for the new deliverable
    setDeliverableToNotify(deliverable);
  };

  const handleDeleteDeliverable = async (id: number) => {
    const success = await deleteDeliverable(id);
    if (success) {
      showToast("Deliverable deleted", "success");
      setDeliverables((prev) => prev.filter((d) => d.id !== id));
    } else {
      showToast("Failed to delete deliverable", "error");
    }
    setOpenMenuId(null);
  };

  const handleStatusChange = async (id: number, newStatus: Deliverable["status"]) => {
    const deliverable = deliverables.find((d) => d.id === id);
    const oldStatus = deliverable?.status;

    const result = await updateDeliverable(id, { status: newStatus });
    if (result) {
      showToast(`Status changed to ${newStatus.replace("_", " ")}`, "success");
      setDeliverables((prev) =>
        prev.map((d) => (d.id === id ? { ...d, status: newStatus } : d))
      );

      // Update sidebar count locally (optimistic update)
      if (oldStatus === "needs_revision" && newStatus !== "needs_revision") {
        updateSchoolCounts(Number(schoolId), { revision: -1 });
      } else if (oldStatus !== "needs_revision" && newStatus === "needs_revision") {
        updateSchoolCounts(Number(schoolId), { revision: 1 });
      }
    } else {
      showToast("Failed to update status", "error");
    }
    setOpenMenuId(null);
  };

  const handleDeliverableUpdate = (updated: Deliverable) => {
    setDeliverables((prev) =>
      prev.map((d) => (d.id === updated.id ? updated : d))
    );
    setSelectedDeliverable(updated);
  };

  // Filter deliverables
  const filteredDeliverables = deliverables.filter((d) => {
    if (typeFilter !== "all" && d.type !== typeFilter) return false;
    if (statusFilter !== "all" && d.status !== statusFilter) return false;
    if (monthFilter !== "all" && d.delivery_month !== parseInt(monthFilter)) return false;
    if (yearFilter !== "all" && d.delivery_year !== parseInt(yearFilter)) return false;
    return true;
  });

  // Get unique years from deliverables for the year filter
  const availableYears = Array.from(
    new Set(deliverables.map((d) => d.delivery_year).filter(Boolean))
  ).sort((a, b) => (b ?? 0) - (a ?? 0));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 size={32} className="animate-spin text-brand" />
      </div>
    );
  }

  if (!school) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">School not found</p>
      </div>
    );
  }

  const schoolName = school.slate_name?.replace(/_/g, " ") || "Unknown School";

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{schoolName}</h1>
            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
              {school.city_name && (
                <span>
                  {[school.area_name, school.city_name].filter(Boolean).join(", ")}
                </span>
              )}
              <button
                onClick={() => setShowContactsModal(true)}
                className="flex items-center gap-1 hover:text-brand transition-colors"
              >
                <Users size={14} />
                {contactsCount} contacts
              </button>
              <span className="flex items-center gap-1">
                <FileText size={14} />
                {deliverables.length} deliverables
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={() => setShowAddDeliverable(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-hover transition-colors"
            >
              <Plus size={16} />
              Add Deliverable
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b px-6">
        <div className="flex gap-1">
          <button
            onClick={() => setActiveTab("deliverables")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "deliverables"
                ? "border-brand text-brand"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <div className="flex items-center gap-2">
              <FileText size={16} />
              Deliverables
              <span className="px-1.5 py-0.5 text-xs bg-gray-100 rounded-full">{deliverables.length}</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab("queries")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "queries"
                ? "border-brand text-brand"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <div className="flex items-center gap-2">
              <HelpCircle size={16} />
              Client Queries
              {openQueriesCount > 0 && (
                <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">
                  {openQueriesCount} open
                </span>
              )}
            </div>
          </button>
        </div>
      </div>

      {/* Filters - only show for deliverables tab */}
      {activeTab === "deliverables" && deliverables.length > 0 && (
        <div className="bg-white border-b px-6 py-3">
          <div className="flex items-center gap-6 flex-wrap">
            {/* Type Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Type:</span>
              <div className="flex items-center gap-1">
                {[
                  { value: "all", label: "All" },
                  { value: "blog", label: "Blogs", icon: FileIcon },
                  { value: "video", label: "Videos", icon: Video },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setTypeFilter(option.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                      typeFilter === option.value
                        ? "bg-brand text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    {option.icon && <option.icon size={14} />}
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Status:</span>
              <div className="flex items-center gap-1">
                {[
                  { value: "all", label: "All" },
                  { value: "approved", label: "Approved", color: "bg-green-500" },
                  { value: "pending", label: "Pending", color: "bg-amber-500" },
                  { value: "needs_revision", label: "Needs Revision", color: "bg-red-500" },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setStatusFilter(option.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                      statusFilter === option.value
                        ? "bg-brand text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    {option.color && (
                      <span className={`w-2 h-2 rounded-full ${option.color}`} />
                    )}
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Month Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Month:</span>
              <select
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand bg-white"
              >
                <option value="all">All</option>
                {monthNames.map((name, i) => (
                  <option key={i} value={i + 1}>{name}</option>
                ))}
              </select>
            </div>

            {/* Year Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Year:</span>
              <select
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand bg-white"
              >
                <option value="all">All</option>
                {availableYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 bg-gray-50">
        {activeTab === "deliverables" ? (
          // Deliverables Tab
          deliverables.length === 0 ? (
            // Empty State
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                <FileText size={32} className="text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No deliverables yet</h3>
              <p className="text-sm text-gray-500 mb-6 max-w-sm">
                Start by adding your first deliverable for this school. You can add blogs or videos.
              </p>
              <button
                onClick={() => setShowAddDeliverable(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-hover transition-colors"
              >
                <Plus size={16} />
              Add Deliverable
            </button>
          </div>
        ) : filteredDeliverables.length === 0 ? (
          // No results after filtering
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-4">
              <FileText size={24} className="text-gray-400" />
            </div>
            <p className="text-gray-500">No deliverables match your filters</p>
            <button
              onClick={() => {
                setTypeFilter("all");
                setStatusFilter("all");
                setMonthFilter("all");
                setYearFilter("all");
              }}
              className="mt-2 text-sm text-brand hover:underline"
            >
              Clear filters
            </button>
          </div>
        ) : (
          // Deliverables Grid
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDeliverables.map((deliverable) => {
              const hasUnread = (deliverable.unread_comment_count ?? 0) > 0;
              const isRevision = deliverable.status === "needs_revision";
              const needsAttention = isRevision || hasUnread;
              const attentionCount = (isRevision ? 1 : 0) + (hasUnread ? 1 : 0);
              return (
              <div
                key={deliverable.id}
                onClick={() => setSelectedDeliverable(deliverable)}
                className={`relative bg-white rounded-xl border overflow-hidden hover:shadow-md transition-shadow cursor-pointer ${
                  isRevision
                    ? "border-red-300 ring-1 ring-red-100"
                    : hasUnread
                    ? "border-blue-300 ring-1 ring-blue-100"
                    : "border-gray-200"
                }`}
              >
                {/* Attention Banner with Count */}
                {needsAttention && (
                  <div className={`px-4 py-1.5 flex items-center justify-between text-xs font-medium ${
                    isRevision
                      ? "bg-red-50 text-red-700"
                      : "bg-blue-50 text-blue-700"
                  }`}>
                    <div className="flex items-center gap-1.5">
                      {isRevision && (
                        <>
                          <AlertCircle size={12} />
                          Client requested revision
                        </>
                      )}
                      {!isRevision && hasUnread && (
                        <>
                          <MessageSquare size={12} />
                          {deliverable.unread_comment_count} new {deliverable.unread_comment_count === 1 ? "comment" : "comments"}
                        </>
                      )}
                      {isRevision && hasUnread && (
                        <span className="ml-1">
                          &bull; {deliverable.unread_comment_count} new {deliverable.unread_comment_count === 1 ? "comment" : "comments"}
                        </span>
                      )}
                    </div>
                    <span className={`min-w-[20px] h-5 flex items-center justify-center px-1.5 text-[11px] font-bold text-white rounded-full ${
                      isRevision ? "bg-red-500" : "bg-blue-500"
                    }`}>
                      {attentionCount}
                    </span>
                  </div>
                )}

                <div className="p-5">
                {/* Card Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center border">
                    {getTypeIcon(deliverable.type)}
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(deliverable.status)}
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId(openMenuId === deliverable.id ? null : deliverable.id);
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                      >
                        <MoreVertical size={16} />
                      </button>
                      {openMenuId === deliverable.id && (
                        <div
                          className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {deliverable.status !== "approved" && (
                            <button
                              onClick={() => handleStatusChange(deliverable.id, "approved")}
                              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                            >
                              <CheckCircle size={14} className="text-green-600" />
                              Mark as Approved
                            </button>
                          )}
                          {deliverable.status !== "pending" && (
                            <button
                              onClick={() => handleStatusChange(deliverable.id, "pending")}
                              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                            >
                              <Clock size={14} className="text-amber-600" />
                              Mark as Pending
                            </button>
                          )}
                          {deliverable.status !== "needs_revision" && (
                            <button
                              onClick={() => handleStatusChange(deliverable.id, "needs_revision")}
                              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                            >
                              <AlertCircle size={14} className="text-red-600" />
                              Needs Revision
                            </button>
                          )}
                          <div className="border-t border-gray-100 my-1" />
                          <button
                            onClick={() => {
                              setDeliverableToNotify(deliverable);
                              setOpenMenuId(null);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Send size={14} className="text-green-600" />
                            Notify Clients
                          </button>
                          <div className="border-t border-gray-100 my-1" />
                          <button
                            onClick={() => handleDeleteDeliverable(deliverable.id)}
                            className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          >
                            <Trash2 size={14} />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Card Content */}
                <h3 className="font-medium text-gray-900 mb-1 line-clamp-2">
                  {deliverable.title}
                </h3>
                {deliverable.delivery_month && deliverable.delivery_year && (
                  <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                    <Calendar size={12} />
                    <span>{monthNames[deliverable.delivery_month - 1]} {deliverable.delivery_year}</span>
                  </div>
                )}
                {deliverable.description && (
                  <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                    {deliverable.description}
                  </p>
                )}

                {/* Card Footer */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <span className="text-sm text-gray-400">
                    {formatDate(deliverable.created_at)}
                  </span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedDeliverable(deliverable);
                      }}
                      className="relative text-gray-400 hover:text-brand transition-colors"
                      title="View comments"
                    >
                      <MessageSquare size={18} />
                      {(deliverable.unread_comment_count ?? 0) > 0 && (
                        <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[11px] font-bold text-white bg-brand rounded-full">
                          {deliverable.unread_comment_count}
                        </span>
                      )}
                    </button>
                    {deliverable.url && (
                      <a
                        href={deliverable.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-gray-400 hover:text-brand transition-colors"
                        title="Open link"
                      >
                        <ExternalLink size={18} />
                      </a>
                    )}
                  </div>
                </div>
                </div>
              </div>
              );
            })}
          </div>
        )
        ) : (
          // Queries Tab
          <div>
          {/* Raise Query Button */}
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setShowRaiseQuery(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-hover transition-colors"
            >
              <Plus size={16} />
              Raise a Query
            </button>
          </div>
          {queriesLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 size={32} className="animate-spin text-brand" />
            </div>
          ) : queries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                <HelpCircle size={32} className="text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No queries yet</h3>
              <p className="text-sm text-gray-500 max-w-sm">
                When the client submits a query, it will appear here for you to respond.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {queries.map((query) => (
                <div
                  key={query.id}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 mb-1">{query.subject}</h3>
                        <p className="text-sm text-gray-500">
                          {query.category && (
                            <span className="inline-block mr-2 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                              {query.category.name}
                            </span>
                          )}
                          Submitted on {new Date(query.created_at).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                          {query.created_by_name && ` by ${query.created_by_name}`}
                          {query.assigned_owner && (
                            <span className="ml-2 text-gray-700">· {query.assigned_owner.name}&apos;s bucket</span>
                          )}
                        </p>
                      </div>
                      {getQueryStatusBadge(query.status)}
                    </div>

                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{linkifyText(query.message)}</p>
                    <AttachmentList attachments={query.attachments} />
                    <div className="mb-4">
                      <QueryRoutingControls
                        query={query}
                        categories={queryCategories}
                        owners={queryOwners}
                        onUpdated={replaceQuery}
                      />
                    </div>

                    {query.status === "resolved" && query.resolved_by_name && (
                      <p className="text-xs text-green-600 mb-4 flex items-center gap-1">
                        <CheckCircle size={12} />
                        Resolved by {query.resolved_by_name}
                        {query.resolved_at && ` on ${new Date(query.resolved_at).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })}`}
                      </p>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <button
                        onClick={() => toggleQueryComments(query.id)}
                        className={`flex items-center gap-1.5 text-sm transition-colors ${
                          expandedQuery === query.id
                            ? "text-brand"
                            : "text-gray-500 hover:text-brand"
                        }`}
                      >
                        <MessageSquare size={14} />
                        View Conversation
                      </button>
                      <div className="flex items-center gap-2">
                        {query.status !== "resolved" && (
                          <>
                            {query.status === "open" && (
                              <button
                                onClick={() => handleQueryStatusChange(query.id, "in_progress")}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              >
                                <Clock size={14} />
                                In Progress
                              </button>
                            )}
                            <button
                              onClick={() => handleQueryStatusChange(query.id, "resolved")}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            >
                              <CheckCircle size={14} />
                              Resolve
                            </button>
                          </>
                        )}
                        {query.status === "resolved" && (
                          <button
                            onClick={() => handleQueryStatusChange(query.id, "open")}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                          >
                            <AlertCircle size={14} />
                            Reopen
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Comments Section */}
                  {expandedQuery === query.id && (
                    <div className="border-t border-gray-200 bg-gray-50 p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Conversation</h4>

                      {/* Comments Thread */}
                      {loadingQueryComments ? (
                        <div className="flex justify-center py-4">
                          <Loader2 size={20} className="animate-spin text-gray-400" />
                        </div>
                      ) : queryComments.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">No messages yet. Start the conversation below.</p>
                      ) : (
                        <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                          {queryComments.map((comment) => (
                            <div
                              key={comment.id}
                              className={`rounded-lg p-3 border ${
                                comment.user_type === "admin"
                                  ? "bg-blue-50 border-blue-100 ml-4"
                                  : "bg-white border-gray-200 mr-4"
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-sm font-medium ${
                                  comment.user_type === "admin" ? "text-blue-700" : "text-gray-900"
                                }`}>
                                  {comment.user_name}
                                  {comment.user_type === "client" && (
                                    <span className="ml-1 text-xs font-normal text-gray-500">(Client)</span>
                                  )}
                                </span>
                                <span className="text-xs text-gray-400">
                                  {new Date(comment.created_at).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                  })}
                                </span>
                              </div>
                              {comment.comment && (
                                <p className={`text-sm whitespace-pre-wrap ${
                                  comment.user_type === "admin" ? "text-blue-800" : "text-gray-700"
                                }`}>{linkifyText(comment.comment)}</p>
                              )}
                              <AttachmentList attachments={comment.attachments} />
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Comment */}
                      <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={queryCommentText}
                          onChange={(e) => setQueryCommentText(e.target.value)}
                          placeholder="Type your response..."
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              handleAddQueryComment();
                            }
                          }}
                        />
                        <button
                          onClick={handleAddQueryComment}
                          disabled={addingQueryComment || (!queryCommentText.trim() && queryCommentFiles.length === 0)}
                          className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 transition-colors"
                        >
                          {addingQueryComment ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Send size={16} />
                          )}
                        </button>
                      </div>
                      <FilePicker files={queryCommentFiles} onChange={setQueryCommentFiles} disabled={addingQueryComment} />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          </div>
        )}
      </div>

      {/* Raise Query Modal */}
      {showRaiseQuery && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Raise a Query</h2>
              <button
                onClick={resetRaiseQuery}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={newQueryCategoryId}
                  onChange={(e) => setNewQueryCategoryId(e.target.value ? Number(e.target.value) : "")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-brand focus:border-brand"
                >
                  <option value="">No category</option>
                  {queryCategories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {c.owners.length > 0 ? ` (${c.owners.map((o) => o.name).join(", ")})` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={newQuerySubject}
                  onChange={(e) => setNewQuerySubject(e.target.value)}
                  placeholder="Enter query subject..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={newQueryMessage}
                  onChange={(e) => setNewQueryMessage(e.target.value)}
                  placeholder="Describe the query..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand resize-none"
                />
              </div>
              <FilePicker files={newQueryFiles} onChange={setNewQueryFiles} disabled={creatingQuery} />
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={resetRaiseQuery}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateQuery}
                disabled={creatingQuery || !newQuerySubject.trim() || !newQueryMessage.trim()}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-hover disabled:opacity-50 transition-colors"
              >
                {creatingQuery ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Submit Query
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Query Response Modal */}
      {selectedQuery && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Respond to Query</h2>
              <button
                onClick={() => {
                  setSelectedQuery(null);
                  setAdminNotes("");
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-1">Subject</p>
                <p className="text-gray-900">{selectedQuery.subject}</p>
              </div>

              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-1">Client's Message</p>
                <p className="text-sm text-gray-600 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">
                  {selectedQuery.message}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Response
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Type your response to the client..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand resize-none"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => {
                  setSelectedQuery(null);
                  setAdminNotes("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleQueryStatusChange(selectedQuery.id, "in_progress", adminNotes)}
                disabled={updatingQuery}
                className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50"
              >
                Save & Keep Open
              </button>
              <button
                onClick={() => handleQueryStatusChange(selectedQuery.id, "resolved", adminNotes)}
                disabled={updatingQuery}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {updatingQuery ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                Resolve Query
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close menu */}
      {openMenuId && (
        <div className="fixed inset-0 z-0" onClick={() => setOpenMenuId(null)} />
      )}

      {/* Notification Contacts Slider */}
      <NotificationContactsModal
        isOpen={showContactsModal}
        onClose={() => setShowContactsModal(false)}
        schoolId={Number(schoolId)}
        schoolName={schoolName}
        onContactsChange={(count) => setContactsCount(count)}
      />

      {/* Add Deliverable Slider */}
      <AddDeliverableSlider
        isOpen={showAddDeliverable}
        onClose={() => setShowAddDeliverable(false)}
        schoolId={Number(schoolId)}
        schoolName={schoolName}
        onSuccess={handleDeliverableCreated}
      />

      {/* Deliverable Detail Slider */}
      <DeliverableDetailSlider
        isOpen={!!selectedDeliverable}
        onClose={async () => {
          setSelectedDeliverable(null);
          // Refresh deliverables to update unread comment counts (admin_read_at was just set)
          const deliverablesData = await getSchoolDeliverables(Number(schoolId));
          setDeliverables(deliverablesData);
        }}
        deliverable={selectedDeliverable}
        schoolId={Number(schoolId)}
        onUpdate={handleDeliverableUpdate}
      />

      {/* Notify Clients Slider */}
      <NotifyClientsSlider
        isOpen={!!deliverableToNotify}
        onClose={() => setDeliverableToNotify(null)}
        deliverable={deliverableToNotify}
        schoolId={Number(schoolId)}
      />
    </div>
  );
};

export default SchoolDetail;
